import React, { useEffect, useRef } from "react";
import useRoleListItems from "@/hooks/useRoleListItems";
import useVersion from "@/hooks/useVersion";
import MenuLink from "./MenuLink";
import useTheme from "@/hooks/useTheme";
import { AdminTheme } from "@/context/ThemeContext";
import {
  Avatar,
  LogOut01,
  OutlineBadge,
  Profile,
  User01
} from "@velaro/velaro-component-lib";
const messagingClientUrl = import.meta.env.VITE_MESSAGING_CLIENT_URL as string;

interface Props {
  profile: Profile;
  onClose(): void;
}

interface ThemeOption {
  id: AdminTheme;
  label: string;
  navColor: string;
  contentColor: string;
}

const THEMES: ThemeOption[] = [
  { id: "light",          label: "Blue",        navColor: "#1670e7", contentColor: "#f8fafc" },
  { id: "dark",           label: "Midnight",    navColor: "#0f172a", contentColor: "#1e293b" },
  { id: "ocean",          label: "Ocean",       navColor: "#3b5d88", contentColor: "#f8fafc" },
  { id: "ocean-dark",     label: "Ocean Dark",  navColor: "#3b5d88", contentColor: "#1e293b" },
  { id: "navy",           label: "Navy",        navColor: "#1a3a5c", contentColor: "#f8fafc" },
  { id: "navy-dark",      label: "Navy Dark",   navColor: "#1a3a5c", contentColor: "#1e293b" },
  { id: "cobalt",         label: "Cobalt",      navColor: "#1a4480", contentColor: "#f8fafc" },
  { id: "cobalt-dark",    label: "Cobalt Dark", navColor: "#1a4480", contentColor: "#1e293b" },
  { id: "teal",           label: "Teal",        navColor: "#0d7373", contentColor: "#f8fafc" },
  { id: "teal-dark",      label: "Teal Dark",   navColor: "#0d7373", contentColor: "#1e293b" },
  { id: "turquoise",      label: "Turquoise",   navColor: "#0e6b7a", contentColor: "#f8fafc" },
  { id: "turquoise-dark", label: "Trq. Dark",   navColor: "#0e6b7a", contentColor: "#1e293b" },
  { id: "forest",         label: "Forest",      navColor: "#1b6d41", contentColor: "#f8fafc" },
  { id: "forest-dark",    label: "Forest Dark", navColor: "#1b6d41", contentColor: "#1e293b" },
  { id: "emerald",        label: "Emerald",     navColor: "#166534", contentColor: "#f8fafc" },
  { id: "emerald-dark",   label: "Emr. Dark",   navColor: "#166534", contentColor: "#1e293b" },
  { id: "sage",           label: "Sage",        navColor: "#3d6b55", contentColor: "#f8fafc" },
  { id: "sage-dark",      label: "Sage Dark",   navColor: "#3d6b55", contentColor: "#1e293b" },
  { id: "seafoam",        label: "Seafoam",     navColor: "#1a7b6b", contentColor: "#f8fafc" },
  { id: "seafoam-dark",   label: "Sea. Dark",   navColor: "#1a7b6b", contentColor: "#1e293b" },
  { id: "slate",          label: "Slate",       navColor: "#334155", contentColor: "#f8fafc" },
  { id: "slate-dark",     label: "Slate Dark",  navColor: "#334155", contentColor: "#1e293b" },
  { id: "grape",          label: "Grape",       navColor: "#5f468c", contentColor: "#f8fafc" },
  { id: "grape-dark",     label: "Grape Dark",  navColor: "#5f468c", contentColor: "#1e293b" },
  { id: "rose",           label: "Rose",        navColor: "#7c3155", contentColor: "#f8fafc" },
  { id: "rose-dark",      label: "Rose Dark",   navColor: "#7c3155", contentColor: "#1e293b" },
];

function ThemePicker() {
  const { theme, setTheme } = useTheme();
  return (
    <div className="mt-3 mb-2">
      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2 px-1">
        Theme
      </div>
      <div className="grid grid-cols-6 gap-1">
        {THEMES.map((t) => {
          const active = theme === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTheme(t.id)}
              title={t.label}
              aria-label={t.label}
              aria-pressed={active}
              className={`rounded overflow-hidden border-2 transition-all focus:outline-none focus:ring-2 focus:ring-cornflower-blue-400/50 ${
                active
                  ? "border-cornflower-blue-500 shadow-sm"
                  : "border-slate-200 hover:border-slate-400"
              }`}
            >
              {/* Mini preview swatch */}
              <div className="flex h-4 overflow-hidden">
                <div className="w-3 h-full flex-shrink-0" style={{ backgroundColor: t.navColor }} />
                <div className="flex-1 h-full relative" style={{ backgroundColor: t.contentColor }}>
                  {active && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <svg className="w-2 h-2" fill="#1670e7" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
              <div className={`px-0.5 py-px ${active ? "bg-cornflower-blue-50" : "bg-white"}`}>
                <span className={`text-[7px] font-medium leading-none block truncate ${
                  active ? "text-cornflower-blue-700" : "text-slate-400"
                }`}>{t.label}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function DesktopProfileMenu({ profile, onClose }: Props) {
  const { getRoleNameById } = useRoleListItems();
  const { version } = useVersion();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.body.addEventListener("click", handleClickOutside);

    return () => {
      document.body.removeEventListener("click", handleClickOutside);
    };
  }, [onClose]);

  const renderRoleBadge = (role: string, index: number) => {
    let roleName = role;
    const parsedNumber = parseInt(role);
    if (!isNaN(parsedNumber)) {
      roleName = getRoleNameById(parsedNumber);
    }

    return <OutlineBadge key={index} label={roleName} size="sm" />;
  };

  const date = new Date();

  return (
    <div
      ref={menuRef}
      className="absolute bottom-0 left-full ml-2 z-20 text-slate-gray-600 shadow-lg border rounded-lg bg-white w-72 p-4"
    >
      <div className="flex items-center mb-4">
        <Avatar
          name={
            profile.firstName && profile.lastName
              ? `${profile.firstName} ${profile.lastName}`
              : profile.displayName
          }
          url={profile.picture}
          size={48}
        />
        <div className="pl-3 flex-grow min-w-0">
          <div className="font-semibold pl-1 break-words">
            {profile.firstName && profile.lastName
              ? `${profile.firstName} ${profile.lastName}`
              : profile.displayName}
          </div>
          <div className="flex flex-wrap items-center">
            {profile.roles?.map(renderRoleBadge)}
          </div>
        </div>
      </div>
      <hr className="border-gray-200" />
      <ThemePicker />
      <hr className="border-gray-200" />
      <div className="whitespace-nowrap">
        <MenuLink
          to={`${messagingClientUrl}/profile`}
          onClose={onClose}
          icon={User01}
          label="Profile Details"
          className="flex items-center text-sm rounded-lg h-12 hover:bg-slate-gray-200 pr-4 py-2 mt-2"
          targetBlank={true}
        />
        <MenuLink
          to="/logout"
          onClose={onClose}
          icon={LogOut01}
          label="Log out"
          className="flex items-center text-sm rounded-lg h-12 hover:bg-slate-gray-200 pr-4 py-2"
        />
      </div>
      <hr className="border-gray-200 mb-3" />
      <div className="flex items-center text-xs text-slate-400">
        &copy; {date.getFullYear()} Velaro v{version}
      </div>
    </div>
  );
}
